import { ConfigOption, FormlyFieldConfig } from '@ngx-formly/core';

import {
  FormlyButtonComponent,
  FormlyCurrencyFieldComponent,
  FormlyCustomCheckboxListComponent,
  FormlyCustomRadioListComponent,
  FormlyDateInputComponent,
  FormlyDatepickerComponent,
  FormlyInputFieldComponent,
  FormlyLtvFieldComponent,
  FormlyMultiFieldComponent,
  FormlyRadioListComponent,
  FormlyRepeatComponent,
  FormlySelectFieldComponent,
  FormlyTemplateComponent,
  FormlyTypeaheadComponent,
  FormlyWorkIncomeRepeatComponent,
} from './fields';
import {
  FormlyAnimationWrapperComponent,
  FormlyCollapseWrapperComponent,
  FormlyFormFieldWrapperComponent,
  FormlyHeaderWrapperComponent,
  FormlyHorizontalWrapperComponent,
  FormlyTableRowWrapperComponent,
} from './wrappers';
import { FormsValidators } from '../forms/validators';
import { errorMessages } from '../../models/error-messages';

export function getFieldName(field: FormlyFieldConfig): string {
  return field.props.text ?? field.props?.addonEnd?.text ?? field.key;
}

export function requiredValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is required`;
}

export function minlengthValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should have at least ${error.requiredLength} characters`;
}

export function maxlengthValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be less than ${error.maxLength} characters`;
}

export function minValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be a minimum of ${error.min}`;
}

export function maxValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be a maximum of ${error.max}`;
}

export function lessThanValidationMessage(error, field: FormlyFieldConfig): string {
  return `The maximum amount allowable is ${(field.formControl.errors.lessThan - 1)
    .toLocaleString('en-US',
      { style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
}

export function greaterThanValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be greater than ${field.formControl.errors.greaterThan}`;
}

export function emailValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid email address`;
}

export function postcodeValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid postcode`;
}

export function moneyValidationMessage(error, field: FormlyFieldConfig): string {
  return `Invalid amount entered for ${getFieldName(field)}`;
}

export function percentageValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid percentage`;
}

export function integerValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} must be a whole number`;
}

export function dateValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid date`;
}

export function minimumAgeValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} must be at least 18 years old`;
}

export function animationExtension(field: FormlyFieldConfig) {
  if (field.wrappers && field.wrappers.includes('animation')) {
    return;
  }

  field.wrappers = ['animation', ...(field.wrappers || [])];
}

export function horizontalExtension(field: FormlyFieldConfig) {
  if (!field.parent) {
    return;
  }

  if (
    field.type === 'repeat' ||
    field.type === 'workincomerepeat' ||
    field.parent?.type === 'repeat' ||
    field.parent?.type === 'workincomerepeat' ||
    field.parent?.parent?.type === 'workincomerepeat' ||
    field.parent?.props?.compositeControl ||
    field.props?.fullWidth
  ) {
    return;
  }

  if (field.wrappers && (field.wrappers.includes('horizontal') || field.wrappers.includes('table-row'))) {
    return;
  }

  field.wrappers = ['horizontal', ...(field.wrappers || [])];
}

const validationMessages = [
  { name: 'required', message: requiredValidationMessage },
  { name: 'minlength', message: minlengthValidationMessage },
  { name: 'maxlength', message: maxlengthValidationMessage },
  { name: 'min', message: minValidationMessage },
  { name: 'max', message: maxValidationMessage },
  { name: 'lessThan', message: lessThanValidationMessage },
  { name: 'greaterThan', message: greaterThanValidationMessage },
  { name: 'email', message: emailValidationMessage },
  { name: 'postcode', message: postcodeValidationMessage },
  { name: 'money', message: moneyValidationMessage },
  { name: 'percentage', message: percentageValidationMessage },
  { name: 'integer', message: integerValidationMessage },
  { name: 'date', message: dateValidationMessage },
  { name: 'atLeast18Years', message: minimumAgeValidationMessage },
];

export const formlyConfigOptions: ConfigOption = {
  types: [
    {
      name: 'button',
      component: FormlyButtonComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'currency',
      component: FormlyCurrencyFieldComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'multiForm',
      component: FormlyMultiFieldComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'customRadio',
      component: FormlyCustomRadioListComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'customCheckbox',
      component: FormlyCustomCheckboxListComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'datepicker',
      component: FormlyDatepickerComponent,
      wrappers: ['form-field'],
      defaultOptions: {
        props: {
          datepickerOptions: {},
        },
        validators: {
          validation: [FormsValidators.date],
        },
      },
    },
    {
      name: 'dateinput',
      component: FormlyDateInputComponent,
      wrappers: ['form-field'],
      defaultOptions: {
        props: {
          datepickerOptions: {},
        },
      },
    },
    {
      name: 'input',
      component: FormlyInputFieldComponent,
      wrappers: ['form-field'],
      defaultOptions: {
        props: {
          datepickerOptions: {},
        },
      },
    },
    {
      name: 'ltv',
      component: FormlyLtvFieldComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'radioList',
      component: FormlyRadioListComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'repeat',
      component: FormlyRepeatComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'select',
      component: FormlySelectFieldComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'template',
      component: FormlyTemplateComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'typeahead',
      component: FormlyTypeaheadComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'workincomerepeat',
      component: FormlyWorkIncomeRepeatComponent,
      wrappers: ['form-field'],
    },
  ],
  wrappers: [
    { name: 'animation', component: FormlyAnimationWrapperComponent },
    { name: 'collapse', component: FormlyCollapseWrapperComponent },
    { name: 'header', component: FormlyHeaderWrapperComponent },
    { name: 'horizontal', component: FormlyHorizontalWrapperComponent },
    { name: 'table-row', component: FormlyTableRowWrapperComponent },
    { name: 'form-field', component: FormlyFormFieldWrapperComponent },
  ],
  extensions: [
    // { name: 'animation', extension: { onPopulate: animationExtension } },
    { name: 'horizontal', extension: { onPopulate: horizontalExtension } },
  ],
  validationMessages: [
    ...validationMessages,
    // Register everything in the 'ErrorMessages' object if a message does not already exist for that name
    ...Object.entries(errorMessages)
      .filter(([name]) => !validationMessages.find(y => y.name === name))
      .map(([name, message]) => ({ name, message })),
  ],
  extras: {
    resetFieldOnHide: true,
  },
};
